import clsx from 'clsx';
import { Check } from 'lucide-react';
import { avertissementsDeLEcole, dateCourte, prenomsEnToutesLettres, type EcoleDesJournees } from '../../../lib/jpa/regles';
import AvertissementsDeLEcole from './AvertissementsDeLEcole';

/*
  Étape 1 : l'école. Une carte par école qui a des journées à venir, avec
  leurs dates et l'avertissement de l'équipe, s'il y en a un. L'école que les
  fiches des enfants indiquent est proposée d'office, et la carte dit
  pourquoi.
*/

interface Props {
  ecoles: EcoleDesJournees[];
  /** Le centre de l'école retenue (choisie ou proposée), ou `null`. */
  choisi: string | null;
  /** Les prénoms des enfants dont la fiche indique chaque école, par identifiant d'école. */
  enfantsParEcole: Map<string, string[]>;
  onChoisir: (centreId: string) => void;
}

export default function ChoixDeLEcole({ ecoles, choisi, enfantsParEcole, onChoisir }: Props) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-extrabold leading-tight">Dans quelle école vont vos enfants ?</h2>

      <fieldset className="space-y-3">
        <legend className="sr-only">L’école</legend>
        {ecoles.map((e) => {
          const actif = e.centreId === choisi;
          const prenoms = e.ecoleId ? enfantsParEcole.get(e.ecoleId) ?? [] : [];
          return (
            <label
              key={e.centreId}
              className={clsx(
                'flex cursor-pointer items-start gap-3 rounded-xl p-4',
                actif ? 'border-2 border-primary-600 bg-primary-50' : 'border border-gray-200 bg-white hover:border-primary-300',
              )}
            >
              <input
                type="radio"
                name="ecole"
                checked={actif}
                onChange={() => onChoisir(e.centreId)}
                className="mt-1 h-5 w-5 flex-shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="flex flex-1 flex-col gap-2">
                <span>
                  <span className="block text-lg font-extrabold leading-tight">{e.ecole}</span>
                  {e.centre !== e.ecole && <span className="block text-sm text-gray-600">{e.centre}</span>}
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {e.journees.map((j) => (
                    <span
                      key={j.sessionId}
                      className={clsx(
                        'rounded-full px-2.5 py-1 text-sm font-bold first-letter:uppercase',
                        actif ? 'border border-primary-200 bg-white text-primary-700' : 'bg-gray-100 text-gray-700',
                      )}
                    >
                      {dateCourte(j.date)}
                    </span>
                  ))}
                </span>
                {prenoms.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-800">
                    <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    L’école de {prenomsEnToutesLettres(prenoms)}, d’après {prenoms.length > 1 ? 'leur fiche' : 'sa fiche'}
                  </span>
                )}
                <AvertissementsDeLEcole avertissements={avertissementsDeLEcole(e.journees)} />
              </span>
            </label>
          );
        })}
      </fieldset>

      <p className="text-sm text-gray-600">
        L’école de vos enfants n’est pas dans la liste ? Éclosion anime les journées pédagogiques dans ses écoles
        partenaires seulement. Une question :{' '}
        <a href="mailto:info@eclosion.be" className="font-semibold text-primary-700 underline">info@eclosion.be</a>
      </p>
    </div>
  );
}
